<template>
  <div
    class="bg-[#2F3637] inline-flex p-4 flex-col justify-center items-center rounded-[var(--radius-rounded-md,6px)] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),0px_2px_4px_-1px_rgba(0,0,0,0.06)]"
  >
    <div class="w-[300px] flex flex-col items-end gap-4">
      <form
        class="w-[300px] flex flex-col items-end gap-[var(--3,12px)] self-stretch"
        @submit.prevent="onSubmit"
      >
        <div
          class="flex w-[300px] h-[143px] flex-col items-start gap-[var(--3,12px)]"
        >
          <div
            class="w-[59px] h-[11px] flex p-0 items-center gap-[var(--25,10px)]"
          >
            <span
              class="font-['Open_Sans'] text-[color:var(--App-system-content,var(--App-ref\_new-neutral-300,#DFDFDF))] text-base not-italic font-semibold leading-5 tracking-[0.016px]"
              >Prompt</span
            >
          </div>
          <div
            class="bg-[#0d0e0e] w-[300px] h-[120px] flex min-h-[80px] max-h-[120px] flex-col items-start self-stretch rounded-[var(--radius-rounded-md,6px)] border border-[color:var(--App-ref\_new-neutral-300,#DFDFDF)] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-solid"
          >
            <RichTextInput
              ref="richTextInputRef"
              class="w[276px] h-[96px] p-[12px] flex items-start gap-2.5 self-stretch flex-[1_0_0] text-[color:var(--App-system-content\_dark,var(--App-ref\_new-neutral-500,#9D9D9D))] text-sm not-italic font-normal leading-5 tracking-[0.25px]"
              :extension-options="{
                bold: false,
                blockquote: false,
                codeBlockHighlight: null,
                customLink: false,
                customMention: ['component'],
                dropEventHandler: false,
                hardbreak: true,
                history: false,
                horizontalRule: false,
                italic: false,
                placeholder: {
                  text: placeholder,
                },
                underline: false,
                toolbarOnSelect: false,
              }"
            />
          </div>
        </div>
        <div class="flex items-start gap-[var(--3,12px)] self-stretch">
          <span
            class="flex-[1_0_0] font-['Open_Sans'] text-[color:var(--App-system-content\_dark,var(--App-ref\_new-neutral-500,#9D9D9D))] text-xs not-italic font-normal leading-4 tracking-[0.4px]"
            >{{ inputLength }}/200 characters</span
          >
          <Button
            class="flex h-8 min-w-[30px] flex-col items-center rounded-[var(--rounded,4px)] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] py-[--25] px-[10px] justify-center"
            :disabled="isLoading"
          >
            <SvgIcon
              v-if="isLoading"
              icon="app-icons/spinner-dots"
              width="16"
              height="16"
              fill="#0D0E0E"
            />
            <Sparkles v-else :size="16" color="#0D0E0E" />
            {{ buttonText }}
          </Button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Button } from '@ui/components/button';
import SvgIcon from '@ui/components/SvgIcon.vue';
import { Sparkles } from 'lucide-vue-next';
import RichTextInput from '../../RichText/RichTextInput.vue';

const props = defineProps({
  placeholder: {
    type: String,
    default: '',
  },
  lastUserPrompt: {
    type: String,
    default: '',
    required: true,
  },
  callback: {
    type: Function,
    required: true,
  },
  buttonText: {
    type: String,
    default: 'Generate',
  },
});

const userInputRef = ref('');
const richTextInputRef = ref(null);
const isLoading = ref(false);

const emit = defineEmits(['suggestion-changed', 'close', 'inputChanged']);
const handleInputChanged = content => {
  emit('inputChanged', content);
};
const handleClose = () => {
  emit('close');
};
const handleSuggestionChanged = state => {
  emit('suggestion-changed', state);
};

const isSuggestionActive = computed(() => {
  let state = richTextInputRef.value?.isSuggestionActive?.();
  if (!state) {
    state = false;
  }
  return state;
});

watch(isSuggestionActive, newState => {
  console.log('AIPopper.vue | isSuggestionActive | watch', newState);
  handleSuggestionChanged(newState);
});

const inputLength = computed(() => {
  if (richTextInputRef.value) {
    const content = richTextInputRef.value.getContent().trim();
    const trimmedContent = content.replace(/^<p>|<\/p>$/g, '');
    const withoutSpan = trimmedContent.replace(/<span[^>]*>|<\/span>/g, '');
    handleInputChanged(content);
    return withoutSpan.length;
  }
  return 0;
});

const onSubmit = async () => {
  if (!isUserPromptValid()) return;
  isLoading.value = true;
  userInputRef.value = getUserPrompt();
  await props.callback(userInputRef.value);
  handleInputChanged('');
  handleClose();
};
function onKeyDown(event) {
  if (event.key === 'Enter') {
    onSubmit();
  }
}
function isUserPromptValid() {
  if (!richTextInputRef.value || isLoading.value || inputLength.value > 200)
    return false;
  return true;
}
function getUserPrompt() {
  let content = richTextInputRef.value.getContent().trim();
  if (!content || content.trim().length === 0) content = null;
  return content;
}
onMounted(() => {
  richTextInputRef.value.setContent(props.lastUserPrompt);
  window.addEventListener('keydown', onKeyDown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyDown);
});
</script>
